import { useMemo, useState, useCallback } from 'react';
import { BigNumber } from 'ethers';
import { Table } from '@tectonicfi/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';
import get from 'lodash/get';

import useAppSelector from '@hooks/useAppSelector';
import { useSupportedAssets } from '@providers/SupportedAssetsProvider';
import { getUnderlyingUsdValue } from '@lib/math';
import { MarketsSliceState } from '@redux/markets';

import MarketsTableRow from '../MarketsTableRow';
import MarketsTableHeader, {
  MarketsTableColumKey,
  mapOrder,
  getAmountInUsdCollections,
  getUtilizationRateCollections,
} from '../MarketsTableHeader';
import { MarketsPageIsolatedPoolData } from '../useMarketsPageData';

interface MarketsTableProps {
  data: MarketsPageIsolatedPoolData;
  supportedAssets: TectonicAsset[];
  updateNetBorrowApy: (symbol: string, netApy: number) => void;
  updateNetSupplyApy: (symbol: string, netApy: number) => void;
}

function MarketsTable({
  data,
  supportedAssets,
  updateNetBorrowApy,
  updateNetSupplyApy,
}: MarketsTableProps): JSX.Element {
  const { map: supportedVENOAssetsMap } = useSupportedAssets('VENO');

  const { borrowAmounts, supplyAmounts, usdPrices } = useAppSelector(
    (
      state
    ): {
      borrowAmounts: MarketsSliceState['borrowAmounts'];
      supplyAmounts: MarketsSliceState['supplyAmounts'];
      usdPrices: Record<string, Undefined<string>>;
    } => {
      return {
        borrowAmounts: get(state, `venoMarkets.borrowAmounts`),
        supplyAmounts: get(state, `venoMarkets.supplyAmounts`),
        usdPrices: state.usdPrices.bySymbol,
      };
    }
  );
  const [orderStatus, setOrderStatus] = useState({
    activeKey: MarketsTableColumKey.default,
    sortOrder: 'desc',
  });

  const orderCollections = useMemo(() => {
    return {
      [MarketsTableColumKey.markets]: supportedVENOAssetsMap,
      [MarketsTableColumKey.totalSupply]: getAmountInUsdCollections(
        supplyAmounts,
        usdPrices,
        supportedVENOAssetsMap
      ),
      [MarketsTableColumKey.totalBorrow]: getAmountInUsdCollections(
        borrowAmounts,
        usdPrices,
        supportedVENOAssetsMap
      ),
      [MarketsTableColumKey.utilization]: getUtilizationRateCollections(
        supplyAmounts,
        borrowAmounts
      ),
      [MarketsTableColumKey.supplyApy]: data.netSupplyApy,
      [MarketsTableColumKey.borrowApy]: data.netBorrowApy,
    };
  }, [
    supportedVENOAssetsMap,
    supplyAmounts,
    usdPrices,
    borrowAmounts,
    data.netSupplyApy,
    data.netBorrowApy,
  ]);

  const sortedSupportedAssets = useMemo(() => {
    return mapOrder(
      supportedAssets,
      orderCollections,
      orderStatus.activeKey,
      orderStatus.sortOrder
    );
  }, [
    supportedAssets,
    orderCollections,
    orderStatus.activeKey,
    orderStatus.sortOrder,
  ]);

  const getTokenUsdPrice = useCallback(
    (symbol: string) =>
      usdPrices[symbol] ? BigNumber.from(usdPrices[symbol]) : null,
    [usdPrices]
  );

  return (
    <div className="m-[-8px] mt-2 flex items-center justify-center bg-gradient-to-r from-rainbowYellow via-rainbowPink to-rainbowPinkSecondary p-1 shadow-market">
      <Table as="div" role="table" className="table border-collapse">
        <MarketsTableHeader
          orderStatus={orderStatus}
          handleOrderStatus={setOrderStatus}
        />
        <Table.Body as="div" className="table-row-group" role="rowgroup">
          {sortedSupportedAssets?.map((asset, index) => {
            if (asset.symbol === 'TUSD') return null;

            const supplyAmount = supplyAmounts[asset.symbol]
              ? BigNumber.from(supplyAmounts[asset.symbol])
              : null;
            const borrowAmount = borrowAmounts[asset.symbol]
              ? BigNumber.from(borrowAmounts[asset.symbol])
              : null;
            const assetUsdPrice = getTokenUsdPrice(asset.symbol);
            return (
              <MarketsTableRow
                key={`${asset.symbol}${index}`}
                mode="VENO"
                asset={asset}
                supplyAmount={supplyAmount}
                supplyInUsd={
                  supplyAmount && assetUsdPrice
                    ? getUnderlyingUsdValue(asset, supplyAmount, assetUsdPrice)
                    : null
                }
                borrowAmount={borrowAmount}
                borrowInUsd={
                  borrowAmount && assetUsdPrice
                    ? getUnderlyingUsdValue(asset, borrowAmount, assetUsdPrice)
                    : null
                }
                updateNetBorrowApy={updateNetBorrowApy}
                updateNetSupplyApy={updateNetSupplyApy}
              />
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}

export default MarketsTable;
